import { AfterViewInit, Component, computed, HostListener, OnDestroy, signal, WritableSignal } from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';

import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';

import { first, Observable, of } from 'rxjs';

import { HeaderService } from '../../services/header.service';
import { MenuService } from '../../services/menu.service';
import { SearchHistoryService } from '../../services/search-history.service';

import { chaveTool, Tool } from '../../models/internal/tool-model';
import { ItemMenu, SubMenu } from '../../models/internal/item-menu-model';
import { AitrusSsoService } from '../../../../services/common/aitrus-sso.service';
import { NotificationService } from '../../../../services/common/notification.service';

import { MenuComponent } from "../menu/menu.component";
import { UserComponent } from "../user/user.component";

/** "Análise" tem que ser achado por quem digita "analise", sem acento e sem caixa. */
const normalizar = (texto: string): string =>
  texto.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase().trim();


@Component({
    selector: 'app-header-2',
    imports: [
        CommonModule,
        FormsModule,
        RouterLink,
        MenuComponent,
        UserComponent,
        MatAutocompleteModule,
        MatFormFieldModule,
        MatButtonModule,
        MatInputModule,
        MatIconModule
    ],
    templateUrl: './header.component.html',
    styleUrl: './header.component.scss',
    animations: [
        trigger('fadeSlideInOut', [
            transition(':enter', [
                style({ opacity: 0, transform: 'translateX(-10px)' }),
                animate('300ms ease-out', style({ opacity: 1, transform: 'translateX(0px)' }))
            ]),
            transition(':leave', [
                animate('300ms ease-out', style({ opacity: 0, transform: 'translateX(-10px)' }))
            ])
        ])
    ]
})
export class HeaderComponent implements AfterViewInit, OnDestroy {
  public scrolled: WritableSignal<boolean> = signal(false);
  // public isMenuVisible: WritableSignal<boolean> = signal(true);
  public isMenuVisible: WritableSignal<boolean>;
  public hasBanner: WritableSignal<boolean> = signal(false);

  private observer!: MutationObserver;

  /** Achatado uma vez: o menu é estático, só a visibilidade de cada item é reativa. */
  private readonly ferramentas: Tool[];

  public busca: WritableSignal<string> = signal('');

  /** `link` não identifica: item de destino Aitrus tem link vazio, e vazio repete. */
  protected readonly chaveTool = chaveTool;

  /** Só com o campo vazio: digitando, o que vale é o que casa com o termo. */
  public recentes = computed(() => this.busca().trim() ? [] : this.historico.recentes());

  public resultados = computed(() => {
    const termo = normalizar(this.busca());

    if (!termo) {
      // Sem termo, a lista completa vem abaixo dos recentes — sem repetir os de cima.
      const recentes = this.historico.recentes();

      return this.ferramentas.filter(f => !recentes.some(r => chaveTool(r) === chaveTool(f)));
    }

    return this.ferramentas.filter(f =>
      normalizar(f.title).includes(termo) || normalizar(f.section ?? '').includes(termo)
    );
  });

  @HostListener('window:scroll', [])
  onWindowScroll() {
    if (window.scrollY > 50) {
      this.scrolled.set(true);
    }
    else {
      this.scrolled.set(false);
    }
  }

  constructor(
    protected headerService: HeaderService,
    private historico: SearchHistoryService,
    private menuService: MenuService,
    private aitrusSso: AitrusSsoService,
    private notificationService: NotificationService,
    private router: Router
  ) {
    this.ferramentas = this.achatarMenu();

    const isDesktop = window.matchMedia('(min-width: 768px)').matches;
    this.isMenuVisible = signal(isDesktop);
    this.headerService.isMenuVisible = this.isMenuVisible;
  }

  ngAfterViewInit() {
    this.observer = new MutationObserver(() => this.updateBannerPresence());
    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });
    this.updateBannerPresence();
  }

  ngOnDestroy() {
    // O observer só nasce no ngAfterViewInit; destruir antes disso não pode estourar.
    this.observer?.disconnect();
  }

  private updateBannerPresence() {
    const activeBanner = Array.from(
      document.getElementsByClassName('ply-widget')
    ).find(el => el.className.includes('ply-visible'));
    this.hasBanner.set(!!activeBanner);
  }

  /**
   * Um item com submenu não é destino: quem navega são os filhos. Sem submenu, o
   * próprio item é a folha, desde que aponte para algum lugar.
   */
  private achatarMenu(): Tool[] {
    return this.menuService.getItemMenu().flatMap((item): Tool[] => {
      if (item.submenu?.length) {
        return item.submenu
          .filter(sub => sub.link || sub.aitrusRedirect)
          .map(sub => ({ title: sub.title, link: sub.link, aitrusRedirect: sub.aitrusRedirect, section: item.title }));
      }

      return item.link || item.externalLink || item.aitrusRedirect
        ? [{ title: item.title, link: item.link, externalLink: item.externalLink, aitrusRedirect: item.aitrusRedirect }]
        : [];
    });
  }

  /** O campo é só um atalho: depois de abrir a ferramenta ele volta a ficar vazio. */
  public semTexto(): string {
    return '';
  }

  public abrir(ferramenta: Tool): void {
    this.busca.set('');
    this.historico.registrar(ferramenta);

    this.findBlockedReason$(ferramenta).pipe(first()).subscribe(reason => {
      if (reason) {
        this.notificationService.sendNotification([{ type: 'blocked', title: ferramenta.title, message: reason }]);
        return;
      }

      this.openDestination(ferramenta);
    });
  }

  /**
   * O bloqueio (`blockedReason$`) sai do menu vivo, casado pela chave, e não do Tool:
   * o Tool vai para o localStorage no histórico, e um observable não sobrevive a isso.
   */
  private findBlockedReason$(ferramenta: Tool): Observable<string | null> {
    // As mesmas folhas do achatarMenu: com submenu, quem conta são os filhos.
    const leaf = this.menuService.getItemMenu()
      .flatMap((item): (ItemMenu | SubMenu)[] => item.submenu?.length ? item.submenu : [item])
      .find(f => chaveTool({
        title: f.title,
        link: f.link,
        externalLink: 'externalLink' in f ? f.externalLink : undefined,
        aitrusRedirect: f.aitrusRedirect
      }) === chaveTool(ferramenta));

    return leaf?.blockedReason$ ?? of(null);
  }

  private openDestination(ferramenta: Tool): void {
    if (ferramenta.aitrusRedirect) {
      this.aitrusSso.abrir(ferramenta.aitrusRedirect).subscribe({
        error: () => this.notificationService.sendNotification([{
          type: 'error',
          title: 'Não foi possível abrir a Aitrus',
          message: 'Tente novamente em instantes. Se persistir, entre em contato com o suporte.'
        }])
      });
      return;
    }

    if (ferramenta.externalLink) {
      window.open(ferramenta.externalLink, '_blank');
      return;
    }

    this.router.navigate([ferramenta.link]);
  }

  public toggleMenu(): void {
    // this.isMenuVisible.update(visible => !visible);
    this.headerService.isMenuVisible.update(visible => !visible);
  }
}
